import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpUtilService } from './http-util.service';
import { CookieService } from 'ngx-cookie-service';
import { filter, map, shareReplay, take, tap } from 'rxjs/operators';
declare function setColors(variables: any): void;
export interface ScaffoldType {
  type: string;
  title: string;
}

export type Environment = {
  production: boolean;
  suppressLog: boolean;
  cognitoUserPoolId: string;
  cognitoClientId: string;
  apiRoot: string;
  socketUrl: string;
  secureCookie: boolean;
  cookieDomain: string;
  name: string;
  domain: string;
  env?: string;
  dd_applicationId?: string;
  dd_clientToken?: string;
  opmReference?: any;
  isOPM?: boolean;
  multiInstance?: boolean;
  instances?: any;
  cognitoSsoUrl?: string;
};

export type InstanceDetails = {
  jsondata?: any;
  title?: string;
  name?: string;
  scaffoldType?: string;
  [x: string]: any;
};

export const Features = [
  {
    label: 'DataX',
    value: 'datax',
    description: 'access to any DataX features',
  },
  {
    label: 'Archive V2',
    value: 'archivev2',
    description: 'access to any Archive V2 features',
  },
  {
    label: 'SIS Archive',
    value: 'sis-archive',
    description: 'access to any SIS Archive features',
  },
  {
    label: 'AI Detection',
    value: 'ai-detection',
    description: 'access to any AI Detection features',
  },
  /* {
    label: 'People',
    value: 'people',
    description: 'Prevents access to any People features',
  },
  {
    label: 'Imports',
    value: 'imports',
    description: 'Prevents access to Imports page',
  },
  {
    label: 'Alerts',
    value: 'alerts',
    description: 'Prevents access to Alerts page',
  },
  {
    label: 'Courses',
    value: 'courses',
    description: 'Prevents access to Courses page',
  },
  {
    label: 'Documentation',
    value: 'documentation',
    description: 'Prevents access to Documentation page',
  },
  {
    label: 'Lakehouse',
    value: 'lakehouse',
    description: 'Prevents access to Lakehouse page',
  },
  {
    label: 'Snowflake',
    value: 'snowflake',
    description: 'Prevents access to Snowflake page',
  },
  {
    label: 'Analytics',
    value: 'analytics',
    description: 'Prevents access to Analytics page',
  },
  {
    label: 'Worksheets',
    value: 'worksheets',
    description: 'Prevents access to Worksheets page',
  },*/
];

export const SystemTypes = [
  'canvas',
  'brightspace',
  'blackboard',
  'moodle',
] as const;

export type SystemType = (typeof SystemTypes)[number];

function detailsEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  if (
    a === null ||
    b === null ||
    typeof a !== 'object' ||
    typeof b !== 'object'
  ) {
    return false;
  }
  if (Array.isArray(a) !== Array.isArray(b)) {
    return false;
  }
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) {
    return false;
  }
  return keys.every(
    key =>
      Object.prototype.hasOwnProperty.call(b, key) &&
      detailsEqual(a[key], b[key])
  );
}

export const ScaffoldTypes: ScaffoldType[] = [
  {
    type: 'archivev2',
    title: 'Canvas Archive',
  },
  {
    type: 'archive',
    title: 'Archive',
  },
  {
    type: 'ai-detection',
    title: 'AI Detection',
  },
  {
    type: 'datax',
    title: 'Data X',
  },
  {
    type: 'designer',
    title: 'Designer',
  },
  {
    type: 'migration',
    title: 'Migration',
  },
];

@Injectable({
  providedIn: 'root',
})
export class EnvironmentService {
  private static readonly SYSTEM_TYPE_STORAGE_KEY = '_scaffold_system_type';

  constructor(
    private http: HttpUtilService,
    private cookieService: CookieService
  ) {
    this.systemTypeOverride = this.loadSystemTypeOverride();
    this.getInstanceDetails()
      .pipe(take(1))
      .subscribe(details => {
        this.setInstanceCookies();
      });
  }
  activeFeature;
  _details: InstanceDetails;
  _types: ScaffoldType[];
  currentType: string;
  selectedType: string;
  systemTypeOverride: SystemType | null = null;
  private fetchingDetails = false;
  private detailsRequest$: Observable<InstanceDetails> | null = null;
  private _detailsSubject = new BehaviorSubject<InstanceDetails | null>(null);
  _typesSubject: BehaviorSubject<string> = new BehaviorSubject<string>(
    this.cookieService.get('school_domain')
  );
  get environment(): Environment {
    return environment;
  }

  /**
   * @description Get the instance details for the current instance
   *
   * @param force Revalidate against the API instead of using the cached details.
   * The revalidated details go to the caller; shared subscribers only hear about
   * details that differ from the ones already published, because a repeat of the
   * current details reads as an instance change to the product selector, and
   * AppComponent.setInstance answers that by navigating away from the page.
   * @returns {Observable<InstanceDetails>}
   */
  getInstanceDetails(force?): Observable<InstanceDetails> {
    if (force) {
      return this.requestInstanceDetails();
    }
    if (!this._details && !this.fetchingDetails) {
      this.requestInstanceDetails().subscribe();
    } else if (this._details && !this._detailsSubject.value) {
      this.publishDetails();
    }
    return this._detailsSubject.pipe(
      filter((details): details is InstanceDetails => details !== null)
    );
  }

  /**
   * Callers that arrive while a request is in flight share it, so a page that
   * asks for fresh details cannot stack up requests for the same payload.
   */
  private requestInstanceDetails(): Observable<InstanceDetails> {
    if (this.detailsRequest$) {
      return this.detailsRequest$;
    }
    this.fetchingDetails = true;
    this.detailsRequest$ = this.http
      .request('get', '/api/institution/details/basic', {}, null, false)
      .pipe(
        map((res: any) => this.adoptInstanceDetails(res)),
        tap({
          next: () => {
            this.fetchingDetails = false;
            this.detailsRequest$ = null;
            this.publishDetails();
          },
          error: () => {
            this.fetchingDetails = false;
            this.detailsRequest$ = null;
          },
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    return this.detailsRequest$;
  }

  private adoptInstanceDetails(res: any): InstanceDetails {
    this.setTheme(res.theme);
    res.original_no_self_serve = res.no_self_serve;
    if (!res.system_type) {
      if (res.scaffoldType === 'archivev2') {
        if (res.no_self_serve) {
          res.system_type = 'brightspace';
        } else {
          res.system_type = 'canvas';
        }
      }
    }
    this._details = res;
    this.currentType = res.scaffoldType;
    if (this.isSystemSwitchExplicitlyDisabled(res) && this.systemTypeOverride) {
      this.systemTypeOverride = null;
      this.persistSystemTypeOverride(null);
    }
    if (!this.activeFeature) {
      this.activeFeature =
        localStorage.getItem('_scaffold_active_feature') || res.scaffoldType;
    }
    return this.getEffectiveDetails();
  }

  /**
   * Subscribers treat any emission as news, so publishing details identical to
   * the ones already out there makes them re-run as though the instance changed.
   */
  private publishDetails(): void {
    const details = this.getEffectiveDetails();
    if (detailsEqual(this._detailsSubject.value, details)) {
      return;
    }
    this._detailsSubject.next(details);
  }

  private loadSystemTypeOverride(): SystemType | null {
    const stored = localStorage.getItem(
      EnvironmentService.SYSTEM_TYPE_STORAGE_KEY
    );
    if (stored && SystemTypes.includes(stored as SystemType)) {
      return stored as SystemType;
    }
    return null;
  }

  private persistSystemTypeOverride(systemType: SystemType | null): void {
    if (systemType) {
      localStorage.setItem(
        EnvironmentService.SYSTEM_TYPE_STORAGE_KEY,
        systemType
      );
    } else {
      localStorage.removeItem(EnvironmentService.SYSTEM_TYPE_STORAGE_KEY);
    }
  }

  isSystemSwitchAllowed(details: InstanceDetails): boolean {
    if (!details) {
      return false;
    }
    if (details.allow_system_switch === true) {
      return true;
    }
    return details.jsondata?.allow_system_switch === true;
  }

  private isSystemSwitchExplicitlyDisabled(details: InstanceDetails): boolean {
    if (!details) {
      return false;
    }
    if (details.allow_system_switch === false) {
      return true;
    }
    return details.jsondata?.allow_system_switch === false;
  }

  private getEffectiveDetails(): InstanceDetails {
    if (!this._details) {
      return this._details;
    }

    if (
      !this.systemTypeOverride ||
      this.isSystemSwitchExplicitlyDisabled(this._details)
    ) {
      return this._details;
    }

    const no_self_serve =
      this.systemTypeOverride === 'blackboard'
        ? true
        : this._details.original_no_self_serve;

    return {
      ...this._details,
      system_type: this.systemTypeOverride,
      no_self_serve,
    };
  }

  setSystemTypeOverride(systemType: SystemType | null): void {
    if (
      !this._details ||
      this.isSystemSwitchExplicitlyDisabled(this._details)
    ) {
      return;
    }
    this.systemTypeOverride = systemType;
    this.persistSystemTypeOverride(systemType);
    this.publishDetails();
  }

  updateInstitutionDetails(updates: Object) {
    this._details = { ...this._details, ...updates };
    this.publishDetails();
  }
  /**
   * @description Get the instance types for the current instance
   * @returns {Observable<ScaffoldType[]>}
   */
  getInstanceTypes(): Observable<ScaffoldType[]> {
    let types = new BehaviorSubject([]);
    if (this._types) {
      types.next(this._types);
    } else {
      this.http
        .request('get', '/api/institution/types', {}, null, false)
        .subscribe((res: any) => {
          this._types = res;
          types.next(res);
        });
    }
    return types.asObservable();
  }
  /**
   * @description Get the domain for the given scaffold type
   * @param type Scaffold type
   * @returns Details for the given scaffold type
   */
  getDetailsForType(type: string) {
    return this._details?.instances
      ? this._details?.instances.find(
          instance => instance.scaffold_type === type
        )
      : '';
  }

  /**
   * @description Get the domain for the given scaffold type
   * @param type Scaffold type
   * @returns Domain for the given scaffold type
   */
  getTypeforDomain(domain: string): string {
    console.log(this._details);
    return this._details?.instances?.find(
      instance => instance.sub_domain === domain
    )?.scaffold_type;
  }

  /**
   * @description Set the selected type and update the cookies and current type accordingly
   * @param type The selected type
   */
  setSelectedType(type: string) {
    this.selectedType = type;
    let details = this.getDetailsForType(this.selectedType);

    this.setCookie('school_domain', details?.sub_domain);
    if (details?.environment) {
      this.setCookie('environment', details.environment);
    } else {
      this.setCookie('environment', null);
    }

    this.currentType = this.selectedType;
    this._typesSubject.next(type);
  }

  watchTypeChange(): Observable<string> {
    return this._typesSubject.asObservable();
  }

  setCookie(key: string, value: string) {
    this.cookieService.set(
      key,
      value,
      null,
      '/',
      this.environment.cookieDomain,
      true
    );
  }

  /**
   * @description Set the instance cookies
   * If the domain exists and is not 'undefined', set the currentType and selectedType to the domain
   * Otherwise, set the currentType and selectedType to the environment domain
   * Also sets the cookies for 'cog_user_pool_id', 'cog_client_id', and 'school_name'
   */
  setInstanceCookies() {
    this.setCookie('cog_user_pool_id', this.environment.cognitoUserPoolId);
    this.setCookie('cog_client_id', this.environment.cognitoClientId);
    this.setCookie('school_name', this.environment.name);
    const active = this.cookieService.get('active_session');
    const authenticated = this.cookieService.get('authenticated');
    const domain = this.cookieService.get('school_domain');
    console.log('domain', domain);
    //if the domain exists and is not 'undefined', set the currentType and selectedType to the domain
    //if the user is not authenticated, set the currentType and selectedType to the environment domain
    if (domain && domain !== 'undefined' && active && authenticated) {
      console.log('domain', domain);
      this.currentType = this.getTypeforDomain(domain);
      this._typesSubject.next(this.currentType);
      this.selectedType = this.currentType;
      console.log('currentType', this.currentType);
    } else {
      this.setCookie('school_domain', this.environment.domain);
      this._typesSubject.next(this.environment.domain);
      this.currentType = this.getTypeforDomain(this.environment.domain);
      this.selectedType = this.currentType;
    }
  }

  setTheme(theme: any) {
    console.log('setting colors form theme', theme);
    if (!theme) return;

    // Create an object to hold CSS variables
    const variables = {};

    variables['--primary'] = theme.$primary;
    variables['--info'] = theme.$info;
    console.log('variables', variables);
    setColors(variables);
  }

  setActiveFeature(feature: string) {
    this.activeFeature = feature;
    localStorage.setItem('_scaffold_active_feature', feature);
  }

  getActiveFeature(): Observable<string> {
    if (!this.activeFeature) {
      this.activeFeature = localStorage.getItem('_scaffold_active_feature');
    }
    return of(this.activeFeature || 'all');
  }

  getFeatures() {
    let features$ = new BehaviorSubject<string[]>([]);
    this.getInstanceDetails()
      .pipe(take(1))
      .subscribe(details => {
        let features = [details.scaffoldType];
        this._details = details;
        if (this._details?.scaffold_features) {
          features.push(...this._details.scaffold_features);
        }
        features$.next(features);
      });
    return features$.asObservable();
  }
  /*
   * @description Check if the instance has the given feature in either the scaffold type or addition features applied to the instance and verify the user is able to see the feature
   * @param feature The feature to check for
   * @returns {Observable<boolean>}
   */
  hasFeature(feature: string | string[], roleData?: any): boolean {
    if (!Array.isArray(feature)) {
      feature = [feature];
    }
    if (roleData?.limit_features) {
      const limit = feature.some(f => roleData?.['limit_' + f] === true);
      if (limit) {
        return false;
      }
    }
    if (this.activeFeature !== 'all') {
      return feature.some(f => f === this.activeFeature);
    } else {
      return feature.some(
        f =>
          this._details?.scaffold_features?.includes(f) ||
          this._details?.scaffoldType === f
      );
    }
  }
}
